import { TFunction } from 'i18next';
import { Check, Paperclip } from 'lucide-react';

import { Badge } from '@/components/ui/badge';

import { getConsignmentStatusBadge } from '@/entities/consignments';
import type { ConsignmentTableData, DangerousGoodSummary, TransportUnitSummary } from '@/entities/consignments';
import { formatDisplayDate } from '@/shared/lib';
import { ColumnPreset, DataTableColumn } from '@/shared/ui/data-table';

const EMPTY_CELL = '-';

/** Legacy prints the raw `true` / `false`; a check mark reads the same and matches the receive pages. */
const renderFlag = (value?: boolean) => (value ? <Check className="h-4 w-4" aria-hidden /> : EMPTY_CELL);

const renderList = (values: string[] | undefined) => (values?.length ? values.join(', ') : EMPTY_CELL);

/**
 * One row per value, as the JSF cell renders its tags. Keeping the entries apart
 * matters for values that contain commas themselves — a consignment line's batch
 * number is frequently a list of its own (`"MPL00009406, MPL00009407"`), so joining
 * would erase the per-line boundaries.
 */
const renderStacked = (values: string[] | undefined, nowrap = false) => {
  if (!values?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {values.map((value, index) => (
        <span key={`${value}-${index}`} className={nowrap ? 'whitespace-nowrap' : undefined}>
          {value}
        </span>
      ))}
    </div>
  );
};

/**
 * `unId — description`, the same shape the receive pages use. The JSF cell shows the bare UN number with the
 * description as its tooltip; the number alone is too cryptic once the column is this wide.
 */
const renderDangerousGoods = (goods: DangerousGoodSummary[] | undefined, descriptions: string[] | undefined) => {
  if (!goods?.length) return renderStacked(descriptions);

  return renderStacked(goods.map((good) => [good.unId, good.description].filter(Boolean).join(' — ')).filter(Boolean));
};

const renderTransportUnits = (units: TransportUnitSummary[] | undefined) =>
  renderStacked(
    units?.map((unit) => `${unit.quantity} ${unit.overpackingType}`),
    true
  );

/**
 * Consignment list columns — JSF parity with the legacy `cmrlist` table
 * (`web/secured/requestlist.xhtml`): same 26 columns in the same order, the same
 * 10 visible by default, the rest surfaced through Customize Columns and the
 * Medical View preset. Sorting follows the legacy `noSort` set, which happens to
 * be a subset of the backend's sort-field whitelist.
 */
export const getColumns = (t: TFunction): DataTableColumn<ConsignmentTableData>[] => [
  {
    id: 'cmrNumber',
    accessorKey: 'cmrNumber',
    header: t('request.cmrNumber.short', 'Consignment'),
    description: 'CMR / Consignment number',
    width: 'min-w-[140px]',
    cell: (row) => <span className="font-mono text-sm">{row.cmrNumber}</span>,
    enableSorting: true,
    sortFieldId: 'cmrNumber'
  },
  {
    id: 'ownersRef',
    accessorKey: 'ownersRef',
    header: t('request.ownersRef.short', 'Reference'),
    description: "Customer / owner's reference",
    width: 'min-w-[130px]',
    cell: (row) => row.ownersRef || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'ownersRef'
  },
  {
    id: 'senderOrgName',
    header: t('request.sender.label', 'Sender'),
    accessorKey: 'senderOrgName',
    description: 'Sender organization',
    width: 'min-w-[150px] max-w-[250px] whitespace-normal',
    cell: (row) => row.senderContact?.organization?.name || EMPTY_CELL,
    sortFieldId: 'sender.org.name'
  },
  {
    id: 'receiverOrgName',
    header: t('request.receiver.label', 'Receiver'),
    accessorKey: 'receiverOrgName',
    description: 'Receiver organization',
    width: 'min-w-[150px] max-w-[250px] whitespace-normal',
    cell: (row) => row.receiverContact?.organization?.name || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'receiver.org.name'
  },
  {
    id: 'arrivalLocationDisplayName',
    accessorKey: 'arrivalLocationDisplayName',
    header: t('newcmr.arrivingAt.label', 'Arriving To'),
    description: 'Arriving-at location',
    width: 'min-w-[200px] max-w-[300px] whitespace-normal',
    cell: (row) => row.arrivalLocationDisplayName || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'arrivalLocation.name'
  },
  {
    id: 'destinationLocationDisplayName',
    accessorKey: 'destinationLocationDisplayName',
    header: t('request.destinationLocation.label', 'Destination'),
    description: 'Destination location',
    width: 'min-w-[200px] max-w-[300px] whitespace-normal',
    cell: (row) => row.destinationLocationDisplayName || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'initialShipment.destination.name'
  },
  {
    id: 'statusCode',
    header: t('request.status.label', 'Status'),
    accessorKey: 'statusName',
    description: 'Request status',
    width: 'min-w-[120px]',
    cell: (row) => {
      const { variant, className } = getConsignmentStatusBadge(row.statusCode);
      return (
        <Badge variant={variant} className={className}>
          {row.statusName || row.statusCode}
        </Badge>
      );
    },
    enableSorting: true,
    sortFieldId: 'status'
  },
  {
    id: 'createdTime',
    accessorKey: 'createdTime',
    header: t('request.createdTime.short', 'Created'),
    description: 'Creation date',
    width: 'min-w-[110px]',
    cell: (row) => <div className="text-xs text-muted-foreground">{formatDisplayDate(row.createdTime)}</div>,
    enableSorting: true,
    sortFieldId: 'createdTime'
  },
  {
    id: 'totalWeightKg',
    accessorKey: 'totalWeightKg',
    header: `${t('request.totalWeight.label', 'Total Weight')} (kg)`,
    description: 'Total weight in kilograms',
    width: 'min-w-[100px]',
    cell: (row) => (typeof row.totalWeightKg === 'number' ? row.totalWeightKg.toFixed(2) : EMPTY_CELL),
    enableSorting: true,
    sortFieldId: 'totalWeight'
  },
  {
    id: 'totalVolumeM3',
    accessorKey: 'totalVolumeM3',
    header: `${t('request.totalVolume.label', 'Total Volume')} (m³)`,
    description: 'Total volume in cubic meters',
    width: 'min-w-[100px]',
    cell: (row) => (typeof row.totalVolumeM3 === 'number' ? row.totalVolumeM3.toFixed(2) : EMPTY_CELL),
    enableSorting: true,
    sortFieldId: 'totalVolume'
  },

  /* --- hidden by default, JSF parity (legacy `data-is-hidden="true"`) -------- */
  {
    id: 'attachments',
    header: t('newcmr.attachments.short', 'Attachments'),
    description: 'Attached files (request + online SRF)',
    width: 'min-w-[110px]',
    cell: (row) => {
      // attachmentCount also counts the online-SRF files, which are not in `attachments`
      const count = row.attachmentCount ?? row.attachments?.length ?? 0;
      if (!count) return EMPTY_CELL;

      const fileNames = row.attachments?.map((attachment) => attachment.fileName).join(', ');

      return (
        <span className="flex items-center gap-1 whitespace-nowrap" title={fileNames || undefined}>
          <Paperclip className="h-4 w-4" aria-hidden />
          {count}
        </span>
      );
    },
    visible: false
  },
  {
    id: 'subprojects',
    header: t('subproject.description.label', 'Sub-project description'),
    description: 'Distinct sub-projects across the lines',
    width: 'min-w-[160px] max-w-[280px] whitespace-normal',
    cell: (row) => renderList(row.subprojectDescriptions),
    visible: false
  },
  {
    id: 'coldChain',
    accessorKey: 'coldChain',
    header: t('requestline.isColdStorage.label', 'Cold Chain'),
    description: 'Any line requires cold-chain storage',
    width: 'min-w-[110px]',
    cell: (row) => renderFlag(row.coldChain),
    visible: false
  },
  {
    id: 'dangerous',
    header: t('requestline.isDangerous.label', 'Dangerous'),
    description: 'Any line is flagged as dangerous goods',
    width: 'min-w-[110px]',
    cell: (row) => renderFlag(row.dangerousGoods),
    visible: false
  },
  {
    id: 'dangerousGoods',
    header: t('tracking.servicereq.dangerous.label', 'Dangerous Goods'),
    description: 'Distinct dangerous goods across the lines',
    // the API sends the master-data description, which runs up to 512 chars — let it wrap
    width: 'min-w-[200px] max-w-[320px] whitespace-normal',
    cell: (row) => renderDangerousGoods(row.dangerousGoodsDetails, row.dangerousGoodsDescriptions),
    visible: false
  },
  {
    id: 'fragile',
    accessorKey: 'fragile',
    header: t('requestline.isFragile.label', 'Fragile'),
    description: 'Any line is flagged as fragile',
    width: 'min-w-[100px]',
    cell: (row) => renderFlag(row.fragile),
    visible: false
  },
  {
    id: 'regulated',
    accessorKey: 'regulated',
    header: t('requestline.isRegulated.label', 'Regulated'),
    description: 'Any line is flagged as regulated',
    width: 'min-w-[110px]',
    cell: (row) => renderFlag(row.regulated),
    visible: false
  },
  {
    id: 'trackIdnf',
    accessorKey: 'trackIdnf',
    header: t('request.trackIdnf.label', 'Tracking Code'),
    description: 'Public tracking identifier',
    width: 'min-w-[130px]',
    cell: (row) => <span className="font-mono text-sm">{row.trackIdnf || EMPTY_CELL}</span>,
    visible: false
  },
  {
    id: 'createdByLogon',
    accessorKey: 'createdByLogon',
    header: t('request.createdByLogon.label', 'Created by'),
    description: 'Logon of the user who created the consignment',
    width: 'min-w-[140px]',
    cell: (row) => row.createdByLogon || EMPTY_CELL,
    visible: false
  },
  {
    id: 'totalInsuranceValueUsd',
    accessorKey: 'totalInsuranceValueUsd',
    header: t('tracking.servicereq.totval.label', 'Total Value (USD)'),
    description: 'Total insurance value in USD',
    width: 'min-w-[120px]',
    cell: (row) =>
      typeof row.totalInsuranceValueUsd === 'number' ? row.totalInsuranceValueUsd.toLocaleString() : EMPTY_CELL,
    visible: false
  },
  {
    id: 'firstExpiryDate',
    accessorKey: 'firstExpiryDate',
    header: t('request.firstExpiryDate.label', 'First Expiry Date'),
    description: 'Earliest line expiry date',
    width: 'min-w-[120px]',
    cell: (row) => <div className="text-xs text-muted-foreground">{formatDisplayDate(row.firstExpiryDate)}</div>,
    visible: false
  },
  {
    id: 'expiryDates',
    header: t('request.expiryDates.label', 'Expiry Dates'),
    description: 'Distinct expiry dates across the lines',
    width: 'min-w-[150px]',
    cell: (row) =>
      row.expiryDates?.length ? (
        <div className="text-xs text-muted-foreground">
          {row.expiryDates.map((date) => formatDisplayDate(date)).join(', ')}
        </div>
      ) : (
        EMPTY_CELL
      ),
    visible: false
  },
  {
    id: 'batchNumbers',
    header: t('request.batchNumbers.label', 'Batch Numbers'),
    description: 'Distinct batch numbers across the lines',
    width: 'min-w-[160px] max-w-[260px] whitespace-normal',
    cell: (row) => renderStacked(row.batchNumbers),
    visible: false
  },
  {
    id: 'transportUnits',
    header: t('tracking.servicereq.overpackingqty.label', 'Transport Unit Quantity'),
    description: 'Overpacking units of the initial shipment',
    width: 'min-w-[150px]',
    cell: (row) => renderTransportUnits(row.transportUnits),
    visible: false
  },
  {
    id: 'remarks',
    accessorKey: 'remarks',
    header: t('requestHistory.remarks.label', 'Remarks'),
    description: 'Latest manual comment from the request history',
    width: 'min-w-[200px] max-w-[320px] whitespace-normal',
    cell: (row) => row.remarks || EMPTY_CELL,
    visible: false
  },
  {
    id: 'estimatedDispatchTime',
    accessorKey: 'estimatedDispatchTime',
    header: `${t('newcmr.arrivalDate.label', 'Expected Arrival Date')}/${t('newcmr.pickupDate.label', 'Pickup Date')}`,
    description: 'Expected service / dispatch start of the initial journey',
    width: 'min-w-[130px]',
    cell: (row) => <div className="text-xs text-muted-foreground">{formatDisplayDate(row.estimatedDispatchTime)}</div>,
    enableSorting: true,
    sortFieldId: 'initialShipment.journey.estDispatchTime',
    visible: false
  }
];

/** Column presets — Medical View is the legacy `data-colset="med"` set, column for column. */
export const getColumnPresets = (t: TFunction): ColumnPreset[] => [
  {
    id: 'medical-view',
    label: t('gen.customize.medview.button', 'Medical View'),
    columnIds: ['cmrNumber', 'attachments', 'totalInsuranceValueUsd', 'firstExpiryDate', 'expiryDates', 'batchNumbers']
  }
];
